import {
    CreateTagRequest,
    CreateTopicRequest,
    CreateTopicResponse,
    DiscoveryLevel,
    FeatureType,
    GetTopicResponse,
    ImageDisplay,
    ObjectId,
    TopicCreationSource
} from '../types';
import { LitElement, css, html } from 'lit';
import { addPoints, getAllPointsPaginated, getTopicImages } from '../helpers/api/points-api';
import { addTopic, getTopic } from '../helpers/api/topics-api';
import { customElement, property, query, state } from 'lit/decorators';
import { getFromLocalStorage, undefinedIfEmpty } from '../helpers/utils';

import { FormInput } from './theme/form-input';
import { FormSwitch } from './theme/form-switch';
import { StateController } from '../helpers/state/state-management/state-controller';
import accountInfo from '../helpers/state/account-info';
import { detectMobileViewport } from '../helpers/device';
import featuresInfo from '../helpers/state/features-info';
import { getCategoryIcon } from '../helpers/markers';
import { getMainPage } from '../helpers/page-name';
import pointCache from '../helpers/state/point-cache';
import topicData from '../helpers/state/topic-data';
import { validateEmail } from '../helpers/strings';

@customElement('duplicate-map-component')
export class DuplicationMapComponent extends LitElement {
    static amplitude_callsite: string = 'Duplicate Map';



    @query('#name')
    private readonly nameElement!: FormInput;

    @query('#headerStyleOption')
    private readonly headerStypeOptionElement!: FormSwitch;

    @query('#shareSettingsOption')
    private readonly shareSettingsOptionElement!: FormSwitch;

    @query('#mapSettingsOption')
    private readonly mapSettingsOptionElement!: FormSwitch;

    @query('#mapShapesOption')
    private readonly mapShapesOptionElement!: FormSwitch;

    @query('#copyAllSettings')
    private readonly copyAllSettingsElement!: FormSwitch;

    @query('#copyAllPointsCategories')
    private readonly copyAllPointsCategoriesElement!: FormSwitch;

    @property({ type: Object })
    topic?: GetTopicResponse;

    @state()
    private selectedCategories: Array<string | undefined> = [];

    @state()
    private loading: boolean = false;

    @state()
    private success: boolean = false;

    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindAccountState = new StateController(this, accountInfo);

    private closeUpgradeModal() {
        this.dispatchEvent(new CustomEvent('close-upgrade'));
    }

    private handleSelectAllCategories(checked: boolean) {
        const categories = this.topic?.categories ?? [];
        this.selectedCategories = checked ? categories.map((c) => c._id?.$oid) : [];
        this.requestUpdate();
    }

    private handleSelectSingleCategory(checked: boolean, oid: string) {
        if (checked) {
            this.selectedCategories.push(oid);
            this.selectedCategories = [...new Set(this.selectedCategories)];
        } else {
            this.selectedCategories = [
                ...new Set(this.selectedCategories.filter((item) => item !== oid))
            ];
        }
        this.requestUpdate();
    }

    private async handleSubmit() {
        const nameValid = this.nameElement.reportValidity();
        const email = undefinedIfEmpty(accountInfo.email) ?? this.topic?.email ?? getFromLocalStorage('email') ?? '';
        const emailValid = validateEmail(email);

        if (!nameValid) {
            getMainPage().errorToastElement.showToastAndThrow(
                'Please fix your invalid inputs (marked red).'
            );
        }

        if (!emailValid) {
            getMainPage().errorToastElement.showToastAndThrow('Invalid email.');
        }

        if (this.topic === undefined) {
            getMainPage().errorToastElement.showToastAndThrow(
                'The topic cannot be duplicated: it is invalid.'
            );
            return;
        }

        const images = await getTopicImages(this.topic?._id.$oid, DuplicationMapComponent.amplitude_callsite, topicData.topicKey);

        const name = this.nameElement.value?.trim() ?? 'My Proxi Map';
        let categories = this.topic.categories ?? [];
        const default_map_center = this.topic.default_map_center ?? {
            search: 'Wichita, KS, USA',
            place_id: 'ChIJLRh_0mrbuocRPj3TdL_VlpM',
            latLng: {
                lat: 37.6871761,
                lng: -97.330053
            }
        };
        const default_map_zoom = this.topic.default_map_zoom || 4;
        const default_map_rotation = this.topic.default_map_rotation || 0;
        const default_map_tilt = this.topic.default_map_tilt || 0;

        // Switch selection from mobile version
        if (detectMobileViewport()) {
            if (this.copyAllPointsCategoriesElement.checked === true) {
                this.selectedCategories = [];
            } else {
                categories = categories.filter((c) =>
                    this.selectedCategories.includes(c._id?.$oid)
                );
            }
            this.headerStypeOptionElement.checked = this.copyAllSettingsElement.checked === false;
            this.mapSettingsOptionElement.checked = this.copyAllSettingsElement.checked === false;
            this.shareSettingsOptionElement.checked = this.copyAllSettingsElement.checked === false;
        } else {
            if (this.copyAllPointsCategoriesElement.checked === true) {
                this.selectedCategories = [];
                categories = this.topic.categories ?? [];
            } else if (this.selectedCategories.length === 0) {
                categories = [categories[0]];
            } else {
                categories = categories.filter((c) =>
                    this.selectedCategories.includes(c._id?.$oid)
                );
            }
        }

        const creator =
            this.headerStypeOptionElement.checked === true ? this.topic.creator : undefined;
        const header_logo =
            this.headerStypeOptionElement.checked === true ? this.topic.header_logo : undefined;
        const header_url =
            this.headerStypeOptionElement.checked === true ? this.topic.header_url : undefined;
        const header_color =
            this.headerStypeOptionElement.checked === true ? this.topic.header_color : undefined;
        const header_text_color =
            this.headerStypeOptionElement.checked === true
                ? this.topic.header_text_color
                : undefined;
        const cluster_accent_color =
            this.mapSettingsOptionElement.checked === true
                ? this.topic.cluster_accent_color
                : undefined;
        const hide_header =
            this.headerStypeOptionElement.checked === true ? this.topic.hide_header : false;
        const show_advanced_filter_row =
            this.mapSettingsOptionElement.checked === true ? this.topic.show_advanced_filter_row : false;
        const custom_font =
            this.mapSettingsOptionElement.checked === true ? this.topic.custom_font : undefined;
        const featured_color =
            this.mapSettingsOptionElement.checked === true ? this.topic.featured_color : undefined;
        const featured_name =
            this.mapSettingsOptionElement.checked === true ? this.topic.featured_name : undefined;
        const custom_map_style =
            this.mapSettingsOptionElement.checked === true
                ? this.topic.custom_map_style
                : undefined;
        const custom_map_style_name =
            this.mapSettingsOptionElement.checked === true
                ? this.topic.custom_map_style_name
                : undefined;
        const custom_shapes =
            this.mapShapesOptionElement.checked === true
                ? this.topic.custom_shapes
                : undefined;
        const custom_overlays =
            this.mapShapesOptionElement.checked === true
                ? this.topic.custom_overlays
                : undefined;
        const marker_cluster_threshold =
            this.mapSettingsOptionElement.checked === true
                ? this.topic.marker_cluster_threshold
                : undefined;
        const marker_cluster_minimum =
            this.mapSettingsOptionElement.checked === true
                ? this.topic.marker_cluster_minimum
                : undefined;
        const add_point_customization =
            this.mapSettingsOptionElement.checked === true
                ? this.topic.add_point_customization
                : undefined;
        const translation_languages =
            this.mapSettingsOptionElement.checked === true
                ? this.topic.translation_languages
                : undefined;
        const send_crowdsource_email =
            this.shareSettingsOptionElement.checked === true
                ? this.topic.send_crowdsource_email
                : true;
        const show_easy_date_filters =
            this.shareSettingsOptionElement.checked === true
                ? this.topic.show_easy_date_filters
                : true;

        const has_dates =
            this.shareSettingsOptionElement.checked === true ? this.topic.has_dates : false;
        const should_filter_past_events =
            this.shareSettingsOptionElement.checked === true
                ? this.topic.should_filter_past_events
                : false;
        const has_time_only =
            this.shareSettingsOptionElement.checked === true ? this.topic.has_time_only : false;
        const has_date_only =
            this.shareSettingsOptionElement.checked === true ? this.topic.has_date_only : false;
        const has_end_date_time = this.shareSettingsOptionElement.checked === true ? this.topic.has_end_date_time : false;
        const has_likes =
            this.shareSettingsOptionElement.checked === true ? this.topic.has_likes : false;
        const disable_google_images =
            this.shareSettingsOptionElement.checked === true
                ? this.topic.disable_google_images
                : false;



        const show_point_list_on_open =
            this.shareSettingsOptionElement.checked === true
                ? this.topic.show_point_list_on_open
                : false;
        const point_sort_order =
            this.shareSettingsOptionElement.checked === true
                ? this.topic.point_sort_order
                : undefined;
        const disable_location_ui =
            this.shareSettingsOptionElement.checked === true
                ? this.topic.disable_location_ui
                : false;
        const requires_approval =
            this.shareSettingsOptionElement.checked === true ? this.topic.requires_approval : false;
        const enable_point_numbering =
            this.shareSettingsOptionElement.checked === true
                ? this.topic.enable_point_numbering
                : false;
        const replace_icons_with_numbers =
            this.shareSettingsOptionElement.checked === true
                ? this.topic.replace_icons_with_numbers
                : false;
        const point_number_circle_color =
            this.shareSettingsOptionElement.checked === true
                ? this.topic.point_number_circle_color
                : undefined;
        const marker_shape =
            this.shareSettingsOptionElement.checked === true
            ? this.topic.marker_shape
                : undefined;
        const default_shape_style =
            this.mapSettingsOptionElement.checked === true ? this.topic.default_shape_style : undefined;
        const search_pill_settings =
            this.mapSettingsOptionElement.checked === true ? this.topic.search_pill_settings : undefined;
        const share_settings =
            this.shareSettingsOptionElement.checked === true ? this.topic.share_settings : undefined;
        const image_display =
            this.mapSettingsOptionElement.checked === true ? this.topic.image_display : ImageDisplay.Cover;
        const description =
            this.shareSettingsOptionElement.checked === true ? this.topic.description : undefined;

        const tag_info: CreateTagRequest = {
            user_tags: this.topic.tag_info.user_tags
        };

        const icon_size =
            this.mapSettingsOptionElement.checked === true ? this.topic.icon_size : undefined;

        const gates = this.topic.gates;

        const topic: CreateTopicRequest = {
            name,
            email,
            organization_id: undefined,
            use_organization_template: false, // Because we are creating a duplicate, do not use template
            creator,
            categories,
            has_dates,
            has_time_only,
            has_date_only,
            show_easy_date_filters,
            has_end_date_time,
            should_filter_past_events,
            hide_bookmark_buttons: this.topic.hide_bookmark_buttons,
            has_likes,
            discovery_level: DiscoveryLevel.Private,
            disable_google_images,
            hide_header,
            show_advanced_filter_row,
            custom_font,
            image_display,
            default_list_view: undefined,
            default_shape_style,
            search_pill_settings,
            share_settings,
            show_point_list_on_open,
            point_sort_order,
            disable_location_ui,
            requires_approval,
            send_crowdsource_email,
            enable_point_numbering,
            replace_icons_with_numbers,
            point_number_circle_color,
            marker_shape,
            default_map_center,
            default_map_zoom,
            default_map_rotation,
            default_map_tilt,
            header_logo,
            header_url,
            header_color,
            header_text_color,
            cluster_accent_color,
            featured_color,
            featured_name,
            custom_map_style,
            custom_map_style_name,
            custom_map_geojson: undefined,
            custom_map_geojson_style: undefined,
            custom_shapes,
            custom_overlays,
            custom_social_media_image: undefined,
            description,
            marker_cluster_threshold,
            marker_cluster_minimum,
            tag_info,
            add_point_customization,
            hide_map_info_drawer: this.topic.hide_map_info_drawer,
            icon_size,
            gates,
            translation_languages,
            pop_ups: this.topic.pop_ups,
            point_email_capture_customization: undefined,
            active_add_ons: this.topic.active_add_ons,
            comment_settings: this.topic.comment_settings,
            disable_powered_by_proxi: this.topic.disable_powered_by_proxi,
            use_simple_filters_desktop: this.topic.use_simple_filters_desktop,
            use_simple_filters_mobile: this.topic.use_simple_filters_mobile,
            hide_keyword_filters: this.topic.hide_keyword_filters,
            text_advanced_filters: this.topic.text_advanced_filters,
            hide_print_button: this.topic.hide_print_button,
            embed_gesture_greedy: this.topic.embed_gesture_greedy,
            enable_panning: this.topic.enable_panning,
            hide_multi_select: this.topic.hide_multi_select,
            text_mark_as_visited: this.topic.text_mark_as_visited,
            text_visited: this.topic.text_visited,
            open_links_in_same_tab: this.topic.open_links_in_same_tab,
        };

        this.loading = true;
        await getMainPage().errorToastElement.tryOrErrorToast(async () => {
            const createTopicResponse: CreateTopicResponse = await addTopic(
                DuplicationMapComponent.amplitude_callsite,
                topic,
                {
                    existing_admin_email: email,
                    creation_source: TopicCreationSource.INTERNAL_DUPLICATE
                }
            );
            const newTopic = await getTopic(
                DuplicationMapComponent.amplitude_callsite,
                createTopicResponse?._id.$oid,
                createTopicResponse.write_key,
                false
            );

            // Duplicate points
            if (this.selectedCategories.length > 0) {
                const pointsToAdd = pointCache.isReady
                    ? pointCache.allPoints
                    : await getAllPointsPaginated(
                        DuplicationMapComponent.amplitude_callsite,
                        this.topic?._id.$oid as string,
                        undefined,
                        undefined,
                        true
                    );
                let newPoints = pointsToAdd.filter((p) => this.selectedCategories.includes(p.topic_category_id.$oid))
                    .map((p) => ({
                        ...p,
                        _id: undefined,
                        featured: p.featured ?? false,
                        roles: undefined,
                        // later we will need to transfer the images too
                        url_images: images.filter((i) => i.entity_id.$oid === p._id.$oid).map((i) => {
                            const uploadImageUrlRequest: { name: string; url: string } = {
                                name: i.name,
                                url: i.sas_url
                            };
                            return uploadImageUrlRequest;
                        }),
                        custom_icon_url: p.custom_icon_url ?? undefined,
                        topic_id: createTopicResponse?._id,
                        topic_category_name: categories.find(
                            (c) => c._id?.$oid === p.topic_category_id.$oid
                        )?.name,
                        tag_info: p.tag_info ?? { user_tags: [], google_tags: [] }
                    }));

                newPoints = newPoints.map((p) => ({
                    ...p,
                    topic_category_id: newTopic.categories.find(
                        (newCategory) => newCategory.name === p.topic_category_name
                    )?._id as ObjectId
                }));

                if (newPoints.length > 0) {
                    await addPoints(
                        DuplicationMapComponent.amplitude_callsite,
                        createTopicResponse?._id.$oid,
                        newPoints,
                        createTopicResponse.write_key
                    );
                }

                this.selectedCategories = [];
            }

            // Open Link
            if (createTopicResponse.should_use_keyless_links) {
                window.open(createTopicResponse.cc_account_admin_link, '_blank');
            } else {
                window.open(createTopicResponse.cc_write_link, '_blank');
            }
        }, 'An error has occurred.  Context: the server rejected the update topic request due to validation issues.');
        this.loading = false;

        this.success = true;
        setTimeout(async () => {
            await this.hardReset();
            this.dispatchEvent(new CustomEvent('on-topic-duplicated'));
        }, 2000);
    }

    async hardReset() {
        // Add all elements back.
        this.success = true;
        await this.requestUpdate();
        this.success = false;

        // Force an update and wait for it to complete.
        await this.requestUpdate();
    }

    protected render() {
        const categories = this.topic?.categories ?? [];
        const hasLinkedListings =
            pointCache.isReady &&
            pointCache.topic?._id.$oid === this.topic?._id.$oid &&
            pointCache.allPoints.some((p) => p.directory_entry_id !== undefined);

        if (this.success) {
            return html`
                <modal-header>Duplicate Your Map</modal-header>
                <div>Duplicated successfully!</div>
            `;
        }

        if (this.loading) {
            return html`
                <loading-element
                    loadingString="Duplicating Map"
                    style="width: 100%; height: 100%;"
                ></loading-element>
            `;
        }

        return html`
            ${featuresInfo.duplicateMapEnabled
                ? html`
                      <div class="duplicateModalContent desktopOnly">
                          <h2 id="duplicateMapTitle">
                              Duplicate <br /><em>${this.topic?.name}</em>
                          </h2>
                          <div>
                              <div class="mapTitleSection">
                                  <form-input
                                      id="name"
                                      type="text"
                                      value="${`${this.topic?.name ?? 'My Proxi Map'} (Copy)`}"
                                      label="New Map Title*"
                                      placeholder="This title will appear at the top of map."
                                      ?required=${true}
                                      maxLength="60"
                                  >
                                  </form-input>
                              </div>
                              <p>Duplicate Settings</p>
                              <p class="notes">Off by default — turn these on to also copy this map's look and setup, not just its pins.</p>
                              <div class="settingSection">
                                  <form-switch id="headerStyleOption" name="headerStyleOption"
                                      >Map Header</form-switch
                                  >
                                  <form-switch id="shareSettingsOption" name="shareSettingsOption"
                                      >Share Settings</form-switch
                                  >
                                  <form-switch id="mapSettingsOption" name="mapSettingsOption"
                                      >Map Settings</form-switch
                                  >
                                  <form-switch id="mapShapesOption" name="mapShapesOption"
                                      >Map Shapes</form-switch
                                  >
                              </div>
                              <p>Duplicate ${topicData.topic?.point_name ?? 'Point'}s</p>
                              ${hasLinkedListings
                                  ? html`<p class="notes">Pins connected to a listing will need to be reconnected on the new map.</p>`
                                  : ''}
                              <div class="categorySection">
                                  <form-checkbox
                                      id="allPointsOption"
                                      name="allPointsOption"
                                      ?checked="${this.selectedCategories.length ===
                                      categories.length}"
                                      ?indeterminate="${this.selectedCategories.length > 0 &&
                                      this.selectedCategories.length < categories.length}"
                                      @change="${(e: any) =>
                                          this.handleSelectAllCategories(
                                              (e.detail as HTMLInputElement).checked
                                          )}"
                                      >All ${topicData.topic?.point_name ?? 'Point'}s</form-checkbox
                                  >
                                  ${categories.map(
                                      (c, index) => html`
                                          <form-checkbox
                                              id="categoriesOption${index}"
                                              name="categoriesOption${index}"
                                              ?checked="${this.selectedCategories.includes(
                                                  c._id?.$oid
                                              )}"
                                              @change="${(e: any) =>
                                                  this.handleSelectSingleCategory(
                                                      (e.detail as HTMLInputElement).checked,
                                                      c._id?.$oid ?? ''
                                                  )}"
                                          >
                                              <div class="categoryItem">
                                                  <img src=${getCategoryIcon(c._id)} />
                                                  <span>${c.name}</span>
                                              </div>
                                          </form-checkbox>
                                      `
                                  )}
                              </div>
                              <form-button
                                  type="submit"
                                  @click="${this.handleSubmit}"
                                  ?fullWidth=${true}
                                  >Duplicate Now</form-button
                              >
                          </div>
                          <error-toast id="errorToast"></error-toast>
                          <div></div>
                      </div>
                      <div class="duplicateModalContent mobileOnly">
                          <h2 id="duplicateMapTitle">Duplicate Map</h2>
                          <div class="mobileSingleSection flexContainer alignCenter mobileOnly">
                              <form-input
                                  id="name"
                                  class="inputSection"
                                  type="text"
                                  value="${`${this.topic?.name ?? 'Proxi Map'} (Copy)`}"
                                  placeholder="This title will appear at the top of map."
                                  ?required=${true}
                              >
                              </form-input>
                          </div>
                          <div class="mobileSingleSection flexContainer alignCenter mobileOnly">
                              <span class="owerTitle">Email Address</span>
                              <form-input
                                  id="email"
                                  class="inputSection"
                                  type="email"
                                  value="${this.topic?.email ?? ''}"
                                  placeholder="This address will receive a secret url to manage and change this map."
                                  ?required=${true}
                              >
                              </form-input>
                          </div>
                          <b>Duplicate all ${topicData.topic?.point_name ?? 'point'}s and categories?</b>
                          <div class="toggleSection flexContainer alignCenter">
                              <span class="switchLabel"> Yes </span>
                              <form-switch
                                  id="copyAllPointsCategories"
                                  name="copyAllPointsCategories"
                                  class="mobileSwitchOption"
                              ></form-switch>
                              <span class="switchLabel"> No, start with an empty map </span>
                          </div>
                          ${hasLinkedListings
                              ? html`<p class="notes">Pins connected to a listing will need to be reconnected on the new map.</p>`
                              : ''}
                          <b>Duplicate all settings?</b>
                          <div class="toggleSection flexContainer alignCenter">
                              <span class="switchLabel"> Yes </span>
                              <form-switch
                                  id="copyAllSettings"
                                  name="copyAllSettings"
                                  class="mobileSwitchOption"
                              ></form-switch>
                              <span class="switchLabel"> No, use defaults </span>
                          </div>
                          <form-button
                              type="submit"
                              @click="${this.handleSubmit}"
                              ?fullWidth=${true}
                              >Create Duplicate Now</form-button
                          >
                      </div>
                  `
                : html`<upgrade-dialog
                      .featureType="${FeatureType.DuplicateMap}"
                      @close="${() => this.closeUpgradeModal()}"
                  ></upgrade-dialog>`}
        `;
    }

    static get styles() {
        return [
            css`
                #duplicateMapTitle {
                    text-align: center;
                    margin-bottom: 1rem;
                    line-height: 36px;
                    color: var(--main-primary-color);
                }

                .mapTitleSection {
                    margin-bottom: 1rem;
                }

                .emailSection {
                    margin-bottom: 1.5rem;
                }

                .settingSection {
                    display: flex;
                    flex-direction: column;
                    margin-bottom: 1.5rem;
                }

                .categorySection {
                    display: flex;
                    flex-wrap: wrap;
                    margin-bottom: 1.5rem;
                }

                .categoryItem {
                    display: flex;
                    align-items: center;
                }

                .categoryItem img {
                    width: 1.25rem;
                    margin-right: 0.25rem;
                }

                form-checkbox {
                    min-width: 30%;
                    padding-right: 3%;
                }

                form-switch {
                    margin-bottom: 5px;
                }

                .duplicateModalContent {
                    padding: 0 1.5rem;
                }

                p {
                    color: var(--theme-input-color);
                    font-weight: 700;
                }

                p.notes {
                    color: var(--theme-lighter-text-color);
                }

                .mobileSingleSection {
                    margin-bottom: 1rem;
                }

                .flexContainer {
                    display: flex;
                }

                .alignCenter {
                    align-items: center;
                }

                .cardImg {
                    margin-right: 1rem;
                    height: 2.5rem;
                }

                .owerTitle {
                    margin-right: 1rem;
                }

                .inputSection {
                    flex: 1;
                }

                .toggleSection {
                    margin-top: 0.75rem;
                    margin-bottom: 1.5rem;
                }

                .switchLabel {
                    flex: 2;
                    text-align: center;
                }

                .mobileSwitchOption {
                    flex: 1;
                    margin-bottom: 0;
                }

                .disabledModal {
                    margin-top: 30px;
                    text-align: center;
                }

                @media (min-width: 551px) {
                    .desktopOnly {
                        display: block;
                    }

                    .mobileOnly {
                        display: none;
                    }
                }

                @media (max-width: 550px) {
                    .desktopOnly {
                        display: none;
                    }

                    .mobileOnly {
                        display: block;
                    }

                    .mobileOnly.flexContainer {
                        display: flex;
                    }

                    .duplicateModalContent {
                        padding: 0;
                    }
                }
            `
        ];
    }
}
